import React, { createRef } from 'react';
import { Grid } from '@material-ui/core';
import { Card, CardHeader, CardContent, LinearProgress, Button, CircularProgress, Select, MenuItem } from '@mui/material';
import StreamingPanel from './StreamingPanel'
import AnnotationPanel from './AnnotationPanel'
import axios from 'axios';

import StreamRecorder from './StreamRecorder';

const STATUS_IDLE = 0
const STATUS_PROCESSING = 1

export default class VideoPrediction extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isProcessing: false,
      isStreaming: false,
      currentFrame: 0,
      error: null,
      frames: new Map(),
      wsConnection: null,
      mediaStream: null,
      availableDevices: [],
      selectedDevice: '',
      
      selectedFrameData: null,
      selectedFrameNumber: null,
      selectedFrame: null,
      selectedFrameOrigin: null,
      selectedFrameDataOrigin: null,

      patientID: "",
      patientName: "",
      sampleID: "",
      collectionDate: "",
      status: STATUS_IDLE,
    };

    this.videoRef = createRef();
    this.canvasRef = createRef();
    this.streamInterval = null;
  }

  async componentDidMount() {
    const {
      patientID,
      patientName,
      sampleID,
      collectionDate
    } = this.props;

    this.setState({
      patientID,
      patientName,
      sampleID,
      collectionDate
    });

    // Lấy danh sách thiết bị camera
    await this.getAvailableDevices();
  }

  componentDidUpdate(prevProps) {
    // So sánh props hiện tại với props trước đó
    if (this.props.patientID !== prevProps.patientID || this.props.patientName !== prevProps.patientName ||
      this.props.sampleID !== prevProps.sampleID || this.props.collectionDate !== prevProps.collectionDate
    ) {
      const {
        patientID,
        patientName,
        sampleID,
        collectionDate
      } = this.props
      this.setState({
        patientID,
        patientName,
        sampleID,
        collectionDate
      })
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Chỉ re-render khi cần thiết
    return (
      this.state.patientID !== nextProps.patientID ||
      this.state.patientName !== nextProps.patientName ||
      this.state.sampleID !== nextProps.sampleID ||
      this.state.collectionDate !== nextProps.collectionDate ||

      this.state.isStreaming !== nextState.isStreaming ||
      this.state.currentFrame !== nextState.currentFrame ||
      this.state.selectedBox !== nextState.selectedBox ||
      this.state.drawingMode !== nextState.drawingMode ||
      this.state.trackingMode !== nextState.trackingMode ||
      this.state.isProcessing !== nextState.isProcessing ||

      this.state.availableDevices !==  nextState.availableDevices ||
      this.state.selectedDevice !==  nextState.selectedDevice ||
      this.state.selectedFrameDataOrigin !==  nextState.selectedFrameDataOrigin ||
      this.state.selectedFrameData !==  nextState.selectedFrameData ||
      this.state.selectedFrameNumber !==  nextState.selectedFrameNumber
    );
  }
  
  componentWillUnmount() {
    this.cleanupResources();
  }

  // Dọn dẹp tất cả tài nguyên
  cleanupResources = () => {
    this.stopFrameCapture();
    this.stopMediaStream();
    this.disconnectWebSocket();
  }

  // Tách rõ việc dừng capture frame và dừng stream
  stopFrameCapture = () => {
    if (this.streamInterval) {
      clearInterval(this.streamInterval);
      this.streamInterval = null;
      console.log('Frame capture stopped');
    }
  }

  // Tách rõ việc dừng media stream
  stopMediaStream = () => {
    if (this.state.mediaStream) {
      this.state.mediaStream.getTracks().forEach(track => {
        try {
          track.stop();
          console.log('Media track stopped:', track.label);
        } catch (error) {
          console.error('Error stopping media track:', error);
        }
      });
    }
    
    // Dừng phát video trước khi xóa srcObject
    if (this.videoRef.current) {
      try {
        const video = this.videoRef.current;
        video.pause();
        
        // Quan trọng: đợi một chút trước khi xóa srcObject
        setTimeout(() => {
          video.srcObject = null;
          // Xóa dữ liệu trên canvas để tránh hiển thị frame cũ
          if (this.canvasRef.current) {
            const ctx = this.canvasRef.current.getContext('2d');
            ctx.clearRect(0, 0, this.canvasRef.current.width, this.canvasRef.current.height);
          }
        }, 50);
      } catch (error) {
        console.error('Error cleaning up video element:', error);
      }
    }
  }

  disconnectWebSocket = () => {
    if (this.state.wsConnection) {
      try {
        // Chỉ đóng kết nối nếu WebSocket ở trạng thái mở hoặc đang kết nối
        if (this.state.wsConnection.readyState === WebSocket.OPEN || 
            this.state.wsConnection.readyState === WebSocket.CONNECTING) {
          console.log('Closing WebSocket connection...');
          this.state.wsConnection.close();
        }
      } catch (error) {
        console.error('Error closing WebSocket:', error);
      } finally {
        // Đảm bảo state luôn được cập nhật ngay cả khi có lỗi
        this.setState({ wsConnection: null });
      }
    }
  }

  getAvailableDevices = async () => {
    try {
      // Yêu cầu quyền truy cập camera trước khi liệt kê thiết bị
      const initialStream = await navigator.mediaDevices?.getUserMedia({ video: true });
      
      // Dừng stream này ngay sau khi lấy danh sách thiết bị để tránh dùng tài nguyên không cần thiết
      initialStream.getTracks().forEach(track => track.stop());
      
      const devices = await navigator.mediaDevices?.enumerateDevices();
      console.log('All devices:', devices);

      const videoDevices = devices.filter(device => {
        const isVideoInput = device.kind === 'videoinput';
        const isUSB = device.label.toLowerCase().includes('usb') || 
                     device.label.toLowerCase().includes('webcam') ||
                     device.label.toLowerCase().includes('camera');
        return isVideoInput && isUSB;
      });

      console.log('USB Video devices:', videoDevices);

      // Nếu không tìm thấy camera USB nào, thử lấy tất cả camera
      if (videoDevices.length === 0) {
        const allVideoDevices = devices.filter(device => device.kind === 'videoinput');
        this.setState({
          availableDevices: allVideoDevices,
          selectedDevice: allVideoDevices[0]?.deviceId || '',
          error: allVideoDevices.length > 0 ? 
            'Không tìm thấy camera USB, hiển thị tất cả camera có sẵn' : 
            'Không tìm thấy camera nào'
        });
        return;
      }

      this.setState({
        availableDevices: videoDevices,
        selectedDevice: videoDevices[0]?.deviceId || ''
      });

    } catch (error) {
      console.error('Error accessing devices:', error);
      this.setState({ 
        error: 'Không thể truy cập thiết bị camera. Lỗi: ' + error.message 
      });
    }
  }

  getDeviceDetails = async (deviceId) => {
    if (!deviceId) {
      console.error('Invalid deviceId provided');
      return null;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: deviceId }
        }
      });
      
      const track = stream.getVideoTracks()[0];
      if (!track) {
        console.error('No video track found');
        stream.getTracks().forEach(t => t.stop());
        return null;
      }
      
      const capabilities = track.getCapabilities();
      const settings = track.getSettings();
      
      console.log('Device Capabilities:', capabilities);
      console.log('Current Settings:', settings);
      
      // Dừng stream sau khi lấy thông tin
      stream.getTracks().forEach(t => t.stop());
      
      return { capabilities, settings };
    } catch (error) {
      console.error('Error getting device details:', error);
      return null;
    }
  }

  handleDeviceChange = async (event) => {
    const deviceId = event.target.value;
    
    // Nếu đang streaming, dừng trước khi thay đổi thiết bị
    if (this.state.isStreaming) {
      await this.stopStream();
    }
    
    // Lấy thông tin chi tiết của thiết bị đã chọn
    const deviceDetails = await this.getDeviceDetails(deviceId);
    
    this.setState({ 
      selectedDevice: deviceId,
      deviceCapabilities: deviceDetails?.capabilities,
      deviceSettings: deviceDetails?.settings
    }, () => {
      // Nếu trước đó đang stream, bắt đầu lại stream với thiết bị mới
      if (this.state.isStreaming) {
        this.startStream();
      }
    });
  }

  startStream = async () => {
    try {
      // Nếu đang streaming, dừng trước
      if (this.state.isStreaming) {
        await this.stopStream();
        
        // Chờ một khoảng thời gian ngắn để các tài nguyên được giải phóng hoàn toàn
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      if (!this.state.selectedDevice) {
        this.setState({ error: 'Vui lòng chọn thiết bị camera' });
        return;
      }

      // Xóa dữ liệu trên canvas để tránh hiển thị frame cũ
      if (this.canvasRef.current) {
        const ctx = this.canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, this.canvasRef.current.width, this.canvasRef.current.height);
      }

      // Đánh dấu đang xử lý để ngăn người dùng nhấn nút nhiều lần
      this.setState({ isProcessing: true, error: null });

      // Lấy thông tin về capabilities của thiết bị
      const deviceDetails = await this.getDeviceDetails(this.state.selectedDevice);
      const capabilities = deviceDetails?.capabilities;

      // Cấu hình constraints dựa trên capabilities của thiết bị
      const constraints = {
        video: {
          deviceId: { exact: this.state.selectedDevice },
          frameRate: { ideal: capabilities?.frameRate?.max || 30 }
        }
      };

      console.log('Using constraints:', constraints);

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (this.videoRef.current) {
        this.videoRef.current.srcObject = stream;
        
        // Đảm bảo video được tải lại và phát trước khi bắt đầu vẽ lên canvas
        this.videoRef.current.onloadedmetadata = () => {
          this.videoRef.current.play().catch(err => {
            console.error('Error playing video:', err);
            this.setState({ error: 'Không thể phát video: ' + err.message });
          });
        };
      }

      // Kết nối WebSocket trước khi cập nhật state
      await this.connectWebSocket();

      this.setState({ 
        mediaStream: stream,
        isStreaming: true,
        isProcessing: false,
        error: null 
      }, async () => {
        // Bắt đầu gửi frames sau khi state đã được cập nhật
        this.startFrameCapture();
        
        // Upload thông tin bệnh nhân
        try {
          const {
            patientID,
            patientName,
            sampleID,
            collectionDate
          } = this.state;

          const formData = new FormData();
          formData.append('patient_id', patientID);
          formData.append('patient_name', patientName);
          formData.append('sample_id', sampleID);
          formData.append('collection_date', collectionDate);

          const response = await axios.post('https://sperms.ailab.vn:5008/upload/', formData);
          
          if (response.status !== 200) {
            console.error('Upload failed with status:', response.status);
          }
        } catch (error) {
          console.error('Error uploading patient info:', error);
          this.setState({ 
            error: 'Lỗi khi gửi thông tin bệnh nhân: ' + (error.message || 'Unknown error')
          });
        }
      });

    } catch (error) {
      console.error('Error accessing camera:', error);
      this.setState({ 
        error: 'Không thể kết nối với camera: ' + (error.message || 'Unknown error'),
        isStreaming: false,
        isProcessing: false
      });
      
      // Dọn dẹp tài nguyên nếu có lỗi
      this.cleanupResources();
    }
  }

  stopStream = async () => {
    console.log('Stopping stream...');
    
    // Chỉ định rõ dừng streaming và đang trong quá trình xử lý
    this.setState({ isProcessing: true });
    
    try {
      // Dừng các hoạt động theo thứ tự
      this.stopFrameCapture();
      this.disconnectWebSocket();
      this.stopMediaStream();
      
      // Cập nhật state
      this.setState({
        mediaStream: null,
        isStreaming: false,
        isProcessing: false,
        frames: new Map(),  // Xóa các frame đã lưu
        currentFrame: 0
      });
      
      console.log('Stream stopped successfully');
    } catch (error) {
      console.error('Error stopping stream:', error);
      this.setState({ 
        error: 'Lỗi khi dừng stream: ' + (error.message || 'Unknown error'),
        isProcessing: false
      });
    }
  }

  startFrameCapture = () => {
    // Dừng interval hiện tại nếu có
    this.stopFrameCapture();
    
    console.log('Starting frame capture...');
    this.streamInterval = setInterval(() => {
      if (this.state.isStreaming && this.state.wsConnection && 
          this.state.wsConnection.readyState === WebSocket.OPEN) {
        this.captureAndSendFrame();
      } else if (!this.state.isStreaming || !this.state.wsConnection || 
                 this.state.wsConnection.readyState !== WebSocket.OPEN) {
        // Nếu không đủ điều kiện để gửi frame, dừng interval
        console.log('Conditions for frame capture not met, stopping...');
        this.stopFrameCapture();
      }
    }, 1000/30); // 30 FPS
  }

  captureAndSendFrame = () => {
    const canvas = this.canvasRef.current;
    const video = this.videoRef.current;
    
    if (!canvas || !video || !this.state.wsConnection || 
        this.state.wsConnection.readyState !== WebSocket.OPEN) {
      return;
    }

    try {
      const ctx = canvas.getContext('2d');
      
      // Đảm bảo video đã sẵn sàng và có kích thước
      if (video.readyState !== video.HAVE_ENOUGH_DATA || 
          video.videoWidth === 0 || video.videoHeight === 0) {
        console.log('Video not ready or has zero dimensions');
        return;
      }
      
      // Đảm bảo canvas có kích thước chính xác của video
      if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        console.log(`Canvas resized to ${canvas.width}x${canvas.height}`);
      }
      
      // Xóa canvas trước khi vẽ frame mới
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(video, 0, 0);

      // Chuyển frame thành blob
      canvas.toBlob(blob => {
        if (!blob || blob.size === 0) {
          console.error('Generated blob is empty');
          return;
        }
        
        // Kiểm tra lại WebSocket trước khi gửi dữ liệu
        if (!this.state.wsConnection || 
            this.state.wsConnection.readyState !== WebSocket.OPEN) {
          console.log('WebSocket not ready, skipping frame send');
          return;
        }
        
        // Chuyển blob thành array buffer và gửi qua WebSocket
        const reader = new FileReader();
        reader.onloadend = () => {
          if (this.state.wsConnection && 
              this.state.wsConnection.readyState === WebSocket.OPEN) {
            this.state.wsConnection.send(reader.result);
          }
        };
        reader.readAsArrayBuffer(blob);
      }, 'image/jpeg', 0.8);
    } catch (error) {
      console.error('Error capturing frame:', error);
    }
  }

  connectWebSocket = async () => {
    // Đóng WebSocket hiện tại nếu có
    this.disconnectWebSocket();
    
    return new Promise((resolve, reject) => {
      try {
        console.log('Connecting to WebSocket...');
        const ws = new WebSocket('wss://sperms.ailab.vn:5005/ws/predict');
        
        // Đặt timeout cho quá trình kết nối
        const connectionTimeout = setTimeout(() => {
          console.error('WebSocket connection timeout');
          this.setState({ 
            error: 'Kết nối tới server quá hạn',
            wsConnection: null
          });
          reject(new Error('WebSocket connection timeout'));
        }, 10000); // 10 giây timeout

        ws.onopen = () => {
          console.log('WebSocket Connected');
          clearTimeout(connectionTimeout);
          this.setState({ 
            wsConnection: ws,
            error: null
          });
          resolve();
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            if (data.type === 'end') {
              this.setState({
                totalFrames: data.total_frames,
                isProcessing: false
              });
              return;
            }

            // Lưu frame và predictions
            this.setState(prevState => {
              const newFrames = new Map(prevState.frames);
              newFrames.set(data.frame_number, {
                image_cv: data.frame_data_cv,
                image: data.frame_data,
                predictions: data.predictions
              });
              return {
                frames: newFrames,
                currentFrame: data.frame_number,
              };
            });

            // Vẽ frame nếu đang playing
            if (this.state.isPlaying) {
              this.drawFrame(data.frame_number);
            }
          } catch (error) {
            console.error('Error processing WebSocket message:', error);
          }
        };

        ws.onerror = (error) => {
          console.error('WebSocket Error:', error);
          clearTimeout(connectionTimeout);
          this.setState({
            error: 'Lỗi kết nối với server: ' + (error.message || 'Unknown error'),
            isProcessing: false,
            wsConnection: null
          });
          reject(error);
        };

        ws.onclose = (event) => {
          console.log('WebSocket closed with code:', event.code);
          clearTimeout(connectionTimeout);
          
          this.setState(prevState => ({
            wsConnection: null,
            error: prevState.isProcessing ? 
              `Kết nối bị ngắt (Code: ${event.code})` : null,
            isProcessing: false
          }));
        };

      } catch (error) {
        console.error('WebSocket connection error:', error);
        this.setState({
          error: 'Không thể kết nối tới server: ' + (error.message || 'Unknown error'),
          isProcessing: false,
          wsConnection: null
        });
        reject(error);
      }
    });
  }

  captureFrame = () => {
    const { currentFrame, frames } = this.state;
    const frameData = frames.get(currentFrame);
    if (frameData) {
      this.setState({
        selectedFrame: frames,
        selectedFrameDataOrigin: frameData,
        selectedFrameData: frameData,
        selectedFrameNumber: currentFrame
      }, () => {
        this.stopStream();
      });
    } else {
      console.warn('No frame data available for frame:', currentFrame);
    }
  }

  generateReport = async () => {
    this.setState({ status: STATUS_PROCESSING, error: null });
    
    try {
      const response = await axios.post(
        'https://sperms.ailab.vn:5008/generate-report',
        {
          patient_id: this.state.patientID,
          patient_name: this.state.patientName,
          sample_id: this.state.sampleID,
          collection_date: this.state.collectionDate,
        },
        {
          responseType: 'blob'
        }
      );

      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
      URL.revokeObjectURL(fileURL);

    } catch (err) {
      console.error('Error generating report:', err);
      this.setState({
        error: 'Có lỗi xảy ra khi tạo báo cáo: ' + (err.message || 'Unknown error')
      });
    } finally {
      this.setState({ status: STATUS_IDLE });
    }
  }

  render() {
    const {
      isProcessing,
      error,
      availableDevices,
      selectedDevice,
      isStreaming,
      mediaStream,
      status
    } = this.state;

    return (
      <div className="w-full px-4 md:px-6 lg:px-8">
        <Card className="w-full">
          <CardHeader
            title="Phân tích Video Stream"
            subheader="Kết nối camera để phát hiện và phân loại"
            className="p-4 md:p-6"
          />

          <CardContent className="p-4 md:p-6">
            <div className="mb-4">
              <Grid container spacing={2} alignItems="center">
                {/* Camera Selection */}
                <Grid item xs={12} md={4}>
                  <Select
                    fullWidth
                    value={selectedDevice}
                    onChange={this.handleDeviceChange}
                    disabled={isStreaming || isProcessing}
                  >
                    {availableDevices.map(device => (
                      <MenuItem key={device.deviceId} value={device.deviceId}>
                        {device.label || `Camera ${device.deviceId.slice(0, 8)}...`}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                {/* Camera Controls */}
                <Grid item xs={12} md={4}>
                  <div className="flex flex-col sm:flex-row gap-2">                  
                    <Button
                      variant="contained"
                      onClick={isStreaming ? this.stopStream : this.startStream}
                      disabled={!selectedDevice || isProcessing}
                      color={isStreaming ? "error" : "primary"}
                      fullWidth
                      className="mb-2 sm:mb-0"
                    >
                      {isProcessing ? 'Đang xử lý...' : 
                       isStreaming ? 'Dừng Camera' : 'Bắt đầu Stream'}
                    </Button>                                      
                  </div>
                </Grid>
                <Grid item xs={12} md={4}>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <StreamRecorder stream={mediaStream} 
                      patientId={this.state.patientID}
                      patientName={this.state.patientName}
                      sampleId={this.state.sampleID} />
                  </div>
                </Grid>
              </Grid>

              {error && (
                <div className="mt-4 text-red-500 p-2 bg-red-50 rounded-md">{error}</div>
              )}
            </div>

            {/* Main Content Grid */}
            <Grid container spacing={3}>
              {/* Video Stream Panel */}
              <Grid item xs={12} lg={6}>
                <div className="relative w-full">
                  <video
                    ref={this.videoRef}
                    autoPlay
                    playsInline
                    hidden
                    className="hidden"
                    muted // Thêm muted để đảm bảo video có thể tự động phát trên mọi trình duyệt
                  />
                  <div className="drawing-mode-container">
                    <canvas
                      ref={this.canvasRef}
                      className="w-full border border-gray-300"
                    />
                  </div>
                </div>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </div>
    );
  }
}